/**
 * Bulls & Cows Game Engine
 * 4-digit secret deduction.
 * RULE: Leading zero is PERMITTED (any 4 distinct digits from '0' to '9').
 * Total permutations: 10 * 9 * 8 * 7 = 5,040 codes.
 */

export class BullsAndCowsEngine {
  constructor(mode = 'practice') {
    this.mode = mode; // 'practice' | 'daily' | 'duel'
    this.reset();
  }

  reset(customSecret = null) {
    if (customSecret && this.isValidCode(customSecret)) {
      this.secret = customSecret;
    } else if (this.mode === 'daily') {
      this.secret = this.generateDailySecret();
    } else {
      this.secret = this.generateRandomSecret();
    }

    this.guessHistory = []; // [{ guess, bulls, cows, timestamp }]
    this.isSolved = false;
  }

  isValidCode(code) {
    if (typeof code !== 'string') return false;
    if (code.length !== 4) return false;
    if (!/^[0-9]{4}$/.test(code)) return false;

    // Check all digits are distinct
    const set = new Set(code.split(''));
    return set.size === 4;
  }

  generateRandomSecret() {
    const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = '';
    for (let i = 0; i < 4; i++) {
      const idx = Math.floor(Math.random() * digits.length);
      result += digits.splice(idx, 1)[0];
    }
    return result;
  }

  generateDailySecret() {
    // Deterministic pseudo-random based on current date YYYY-MM-DD
    const dateStr = new Date().toISOString().split('T')[0];
    let hash = 0;
    for (let i = 0; i < dateStr.length; i++) {
      hash = (hash << 5) - hash + dateStr.charCodeAt(i);
      hash |= 0;
    }

    const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = '';
    let seed = Math.abs(hash);

    for (let i = 0; i < 4; i++) {
      seed = (seed * 9301 + 49297) % 233280;
      const idx = Math.floor((seed / 233280) * digits.length);
      result += digits.splice(idx, 1)[0];
    }
    return result;
  }

  evaluateGuess(guess, targetSecret = this.secret) {
    if (!this.isValidCode(guess)) {
      return { error: 'Invalid guess: must be exactly 4 unique digits' };
    }

    let bulls = 0;
    let cows = 0;

    for (let i = 0; i < 4; i++) {
      const gChar = guess[i];
      const sChar = targetSecret[i];

      if (gChar === sChar) {
        bulls++;
      } else if (targetSecret.includes(gChar)) {
        cows++;
      }
    }

    const win = bulls === 4;
    const entry = { guess, bulls, cows, win, timestamp: Date.now() };
    this.guessHistory.push(entry);

    if (win) {
      this.isSolved = true;
    }

    return entry;
  }

  serialize() {
    return {
      secret: this.secret,
      mode: this.mode,
      guessHistory: [...this.guessHistory],
      isSolved: this.isSolved
    };
  }

  restore(data) {
    if (!data) return;
    this.secret = data.secret;
    this.mode = data.mode;
    this.guessHistory = [...(data.guessHistory || [])];
    this.isSolved = !!data.isSolved;
  }
}
